import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { storage } from '../utils/storage';
import { buildApiUrl } from '../config/env';
import type { QuoteAttachmentPayload } from '../types/work';

export interface Job {
  id: string;
  type: 'contract' | 'product' | 'supply';
  title: string;
  category: string;
  description: string;
  budget?: number;
  price?: number;
  images?: string[];
  status: 'open' | 'closed' | 'sold';
  
  // Contract-specific fields
  projectLevel?: string;
  duration?: string;
  contractType?: string;
  locationType?: string;
  workAddress?: string;
  experienceYears?: number;
  projectExpiryDate?: string;
  projectDeadlineDate?: string;
  contractPdfUrl?: string;
  contractPdfName?: string;
  contractPdfSize?: number;
  
  // Supply-specific fields
  quantity?: number;
  unit?: string;
  
  createdAt: Date;
  createdBy: {
    id: string;
    name?: string;
    profession?: string;
    photo?: string;
  };
  _count?: {
    quotes: number;
  };
}

export interface Quote {
  id: string;
  jobId: string;
  amount: number;
  message?: string;
  attachmentUrl?: string;
  attachmentName?: string;
  attachmentSize?: number;
  status: 'sent' | 'viewed' | 'accepted' | 'completion_requested' | 'completed';
  
  // Completion tracking
  completionRequestedBy?: string;
  completionRequestedAt?: Date;
  completedAt?: Date;
  
  createdAt: Date;
  job?: {
    id: string;
    type: string;
    title: string;
    category: string;
    status: string;
    createdBy: {
      id: string;
      name?: string;
      photo?: string;
    };
  };
  freelancer?: {
    id: string;
    name?: string;
    profession?: string;
    photo?: string;
  };
}

interface WorkContextType {
  // Jobs
  jobs: Job[];
  myJobs: Job[];
  loadingJobs: boolean;
  hasMoreJobs: boolean;
  fetchJobs: (type?: 'contract' | 'product', category?: string, loadMore?: boolean) => Promise<void>;
  fetchJobById: (id: string) => Promise<Job | null>;
  fetchMyJobs: () => Promise<void>;
  fetchUserJobs: (userId: string) => Promise<Job[]>;
  createJob: (data: Partial<Job>, pdfFile?: { uri: string; name: string; size: number }) => Promise<Job>;
  updateJob: (id: string, data: Partial<Job>, pdfFile?: { uri: string; name: string; size: number } | null) => Promise<Job>;
  deleteJob: (id: string) => Promise<void>;
  
  // Quotes
  myQuotes: Quote[];
  loadingQuotes: boolean;
  fetchMyQuotes: () => Promise<void>;
  fetchJobQuotes: (jobId: string) => Promise<Quote[]>;
  submitQuote: (jobId: string, amount: number, message?: string, attachment?: QuoteAttachmentPayload) => Promise<Quote>;
  updateQuote: (
    quoteId: string,
    amount: number,
    message?: string,
    attachment?: QuoteAttachmentPayload | null
  ) => Promise<Quote>;
  updateQuoteStatus: (quoteId: string, status: 'viewed' | 'accepted') => Promise<Quote>;
  acceptQuote: (quoteId: string) => Promise<Quote>;
  requestCompletion: (quoteId: string) => Promise<Quote>;
  confirmCompletion: (quoteId: string) => Promise<Quote>;
  deleteQuote: (quoteId: string) => Promise<void>;
}

const WorkContext = createContext<WorkContextType | undefined>(undefined);

export const useWork = () => {
  const context = useContext(WorkContext);
  if (!context) {
    throw new Error('useWork must be used within a WorkProvider');
  }
  return context;
};

interface WorkProviderProps {
  children: ReactNode;
}

export const WorkProvider: React.FC<WorkProviderProps> = ({ children }) => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [myJobs, setMyJobs] = useState<Job[]>([]);
  const [myQuotes, setMyQuotes] = useState<Quote[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [hasMoreJobs, setHasMoreJobs] = useState(true);
  const [currentOffset, setCurrentOffset] = useState(0);
  const JOBS_PER_PAGE = 10;

  const fetchJobs = async (type?: 'contract' | 'product', category?: string, loadMore: boolean = false) => {
    try {
      setLoadingJobs(true);
      
      const offset = loadMore ? currentOffset : 0;
      
      const queryParams = new URLSearchParams();
      if (type) queryParams.append('type', type);
      if (category) queryParams.append('category', category);
      queryParams.append('limit', JOBS_PER_PAGE.toString());
      queryParams.append('offset', offset.toString());

      const response = await fetch(buildApiUrl(`api/jobs?${queryParams.toString()}`));
      const data = await response.json();

      if (data.success) {
        const newJobs = data.jobs.map((job: any) => ({
          ...job,
          createdAt: new Date(job.createdAt),
        }));
        
        if (loadMore) {
          setJobs(prev => [...prev, ...newJobs]);
        } else {
          setJobs(newJobs);
          setCurrentOffset(0);
        }
        
        setCurrentOffset(offset + newJobs.length);
        setHasMoreJobs(newJobs.length === JOBS_PER_PAGE);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      throw error;
    } finally {
      setLoadingJobs(false);
    }
  };

  const fetchJobById = async (id: string): Promise<Job | null> => {
    try {
      const response = await fetch(buildApiUrl(`api/jobs/${id}`));
      const data = await response.json();

      if (data.success && data.job) {
        return {
          ...data.job,
          createdAt: new Date(data.job.createdAt),
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching job:', error);
      return null;
    }
  };

  const fetchMyJobs = async () => {
    try {
      setLoadingJobs(true);
      const token = await storage.getToken();
      
      const response = await fetch(buildApiUrl('api/jobs/my-jobs'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      const data = await response.json();

      if (data.success) {
        setMyJobs(data.jobs.map((job: any) => ({
          ...job,
          createdAt: new Date(job.createdAt),
        })));
      }
    } catch (error) {
      console.error('Error fetching my jobs:', error);
      throw error;
    } finally {
      setLoadingJobs(false);
    }
  };

  const fetchUserJobs = async (userId: string): Promise<Job[]> => {
    try {
      const response = await fetch(buildApiUrl(`api/jobs/user/${userId}`));
      const data = await response.json();

      if (data.success) {
        return data.jobs.map((job: any) => ({
          ...job,
          createdAt: new Date(job.createdAt),
        }));
      }
      return [];
    } catch (error) {
      console.error('Error fetching user jobs:', error);
      return [];
    }
  };

  const createJob = async (jobData: Partial<Job>, pdfFile?: { uri: string; name: string; size: number }): Promise<Job> => {
    try {
      const token = await storage.getToken();
      
      let response;

      if (pdfFile) {
        // PDF upload
        const formData = new FormData();
        
        // Append all job data fields directly to formData
        Object.keys(jobData).forEach(key => {
          const value = (jobData as any)[key];
          if (value !== undefined && value !== null) {
            formData.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
          }
        });
        
        // Append the PDF file with correct field name
        formData.append('contractPdf', {
          uri: pdfFile.uri,
          type: 'application/pdf',
          name: pdfFile.name,
        } as any);

        response = await fetch(buildApiUrl('api/jobs'), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });
      } else {
        // Regular JSON upload
        response = await fetch(buildApiUrl('api/jobs'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(jobData),
        });
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to create job');
      }

      const newJob = { ...data.job, createdAt: new Date(data.job.createdAt) };
      
      // Refresh my jobs list
      await fetchMyJobs();

      return newJob;
    } catch (error) {
      console.error('Error creating job:', error);
      throw error;
    }
  };

  const updateJob = async (id: string, jobData: Partial<Job>, pdfFile?: { uri: string; name: string; size: number } | null): Promise<Job> => {
    try {
      const token = await storage.getToken();
      
      let response;
      
      if ((pdfFile || pdfFile === null) && jobData.type === 'contract') {
        // Use FormData for PDF upload/removal
        const formData = new FormData();
        
        // Add all job fields
        Object.entries(jobData).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
          }
        });
        
        // Add PDF file or removal flag
        if (pdfFile === null) {
          formData.append('removeContractPdf', 'true');
        } else if (pdfFile) {
          formData.append('contractPdf', {
            uri: pdfFile.uri,
            type: 'application/pdf',
            name: pdfFile.name,
          } as any);
        }
        
        response = await fetch(buildApiUrl(`api/jobs/${id}`), {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });
      } else {
        // Regular JSON upload
        response = await fetch(buildApiUrl(`api/jobs/${id}`), {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(jobData),
        });
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to update job');
      }

      const updatedJob = { ...data.job, createdAt: new Date(data.job.createdAt) };
      
      // Refresh my jobs list
      await fetchMyJobs();

      return updatedJob;
    } catch (error) {
      console.error('Error updating job:', error);
      throw error;
    }
  };

  const deleteJob = async (id: string): Promise<void> => {
    try {
      const token = await storage.getToken();
      
      const response = await fetch(buildApiUrl(`api/jobs/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to delete job');
      }

      // Refresh my jobs list
      await fetchMyJobs();
    } catch (error) {
      console.error('Error deleting job:', error);
      throw error;
    }
  };

  const fetchMyQuotes = async () => {
    try {
      setLoadingQuotes(true);
      const token = await storage.getToken();
      
      const response = await fetch(buildApiUrl('api/quotes/my-quotes'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      const data = await response.json();

      if (data.success) {
        setMyQuotes(data.quotes.map((quote: any) => ({
          ...quote,
          createdAt: new Date(quote.createdAt),
        })));
      }
    } catch (error) {
      console.error('Error fetching my quotes:', error);
      throw error;
    } finally {
      setLoadingQuotes(false);
    }
  };

  const fetchJobQuotes = async (jobId: string): Promise<Quote[]> => {
    try {
      const token = await storage.getToken();
      
      const response = await fetch(buildApiUrl(`api/quotes/job/${jobId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      const data = await response.json();

      if (data.success) {
        return data.quotes.map((quote: any) => ({
          ...quote,
          createdAt: new Date(quote.createdAt),
        }));
      }
      return [];
    } catch (error) {
      console.error('Error fetching job quotes:', error);
      return [];
    }
  };

  const submitQuote = async (
    jobId: string,
    amount: number,
    message?: string,
    attachment?: QuoteAttachmentPayload
  ): Promise<Quote> => {
    try {
      const token = await storage.getToken();
      const payload: Record<string, unknown> = { jobId, amount, message };

      if (attachment) {
        payload.attachment = {
          url: attachment.url,
          name: attachment.name,
          size: attachment.size,
        };
      }
      
      const response = await fetch(buildApiUrl('api/quotes'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to submit quote');
      }

      const newQuote = { ...data.quote, createdAt: new Date(data.quote.createdAt) };
      
      // Refresh quotes list
      await fetchMyQuotes();

      return newQuote;
    } catch (error) {
      console.error('Error submitting quote:', error);
      throw error;
    }
  };

  const updateQuoteStatus = async (quoteId: string, status: 'viewed' | 'accepted'): Promise<Quote> => {
    try {
      const token = await storage.getToken();
      
      const response = await fetch(buildApiUrl(`api/quotes/${quoteId}/status`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to update quote status');
      }

      const updatedQuote = { ...data.quote, createdAt: new Date(data.quote.createdAt) };
      
      // Refresh quotes and jobs
      await fetchMyQuotes();
      await fetchMyJobs();

      return updatedQuote;
    } catch (error) {
      console.error('Error updating quote status:', error);
      throw error;
    }
  };

  const acceptQuote = async (quoteId: string): Promise<Quote> => {
    try {
      const token = await storage.getToken();
      
      const response = await fetch(buildApiUrl(`api/quotes/${quoteId}/status`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status: 'accepted' }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to accept quote');
      }

      const acceptedQuote = { ...data.quote, createdAt: new Date(data.quote.createdAt) };
      
      // Refresh quotes and jobs
      await fetchMyQuotes();
      await fetchMyJobs();

      return acceptedQuote;
    } catch (error) {
      console.error('Error accepting quote:', error);
      throw error;
    }
  };

  const updateQuote = async (
    quoteId: string,
    amount: number,
    message?: string,
    attachment?: QuoteAttachmentPayload | null
  ): Promise<Quote> => {
    try {
      const token = await storage.getToken();
      const payload: Record<string, unknown> = { amount, message };

      if (typeof attachment !== 'undefined') {
        payload.attachment = attachment
          ? {
              url: attachment.url,
              name: attachment.name,
              size: attachment.size,
            }
          : null;
      }
      
      const response = await fetch(buildApiUrl(`api/quotes/${quoteId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to update quote');
      }

      const updatedQuote = { ...data.quote, createdAt: new Date(data.quote.createdAt) };
      
      // Refresh quotes list
      await fetchMyQuotes();

      return updatedQuote;
    } catch (error) {
      console.error('Error updating quote:', error);
      throw error;
    }
  };

  const requestCompletion = async (quoteId: string): Promise<Quote> => {
    try {
      const token = await storage.getToken();
      
      const response = await fetch(buildApiUrl(`api/quotes/${quoteId}/request-completion`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to request completion');
      }

      const updatedQuote = { 
        ...data.quote, 
        createdAt: new Date(data.quote.createdAt),
        completionRequestedAt: data.quote.completionRequestedAt ? new Date(data.quote.completionRequestedAt) : undefined,
      };
      
      // Refresh quotes and jobs
      await fetchMyQuotes();
      await fetchMyJobs();

      return updatedQuote;
    } catch (error) {
      console.error('Error requesting completion:', error);
      throw error;
    }
  };

  const confirmCompletion = async (quoteId: string): Promise<Quote> => {
    try {
      const token = await storage.getToken();
      
      const response = await fetch(buildApiUrl(`api/quotes/${quoteId}/confirm-completion`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to confirm completion');
      }

      const completedQuote = { 
        ...data.quote, 
        createdAt: new Date(data.quote.createdAt),
        completionRequestedAt: data.quote.completionRequestedAt ? new Date(data.quote.completionRequestedAt) : undefined,
        completedAt: data.quote.completedAt ? new Date(data.quote.completedAt) : undefined,
      };
      
      // Refresh quotes and jobs
      await fetchMyQuotes();
      await fetchMyJobs();

      return completedQuote;
    } catch (error) {
      console.error('Error confirming completion:', error);
      throw error;
    }
  };

  const deleteQuote = async (quoteId: string): Promise<void> => {
    try {
      const token = await storage.getToken();
      
      const response = await fetch(buildApiUrl(`api/quotes/${quoteId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message || 'Failed to delete quote');
      }

      // Refresh quotes list
      await fetchMyQuotes();
    } catch (error) {
      console.error('Error deleting quote:', error);
      throw error;
    }
  };

  const value: WorkContextType = {
    jobs,
    myJobs,
    loadingJobs,
    hasMoreJobs,
    fetchJobs,
    fetchJobById,
    fetchMyJobs,
    fetchUserJobs,
    createJob,
    updateJob,
    deleteJob,
    myQuotes,
    loadingQuotes,
    fetchMyQuotes,
    fetchJobQuotes,
    submitQuote,
    updateQuote,
    updateQuoteStatus,
    acceptQuote,
    requestCompletion,
    confirmCompletion,
    deleteQuote,
  };

  return <WorkContext.Provider value={value}>{children}</WorkContext.Provider>;
};
